// Who gets told when attendance has not been uploaded.
//
// The biometric export is the whole month's grid, pulled on a habit day. So
// the reminder cannot ask "is the period complete?" — the month is not
// complete until it ends. It asks "is attendance current?", and the answer is
// a date: the last day attendance actually reaches.
//
// What this screen does NOT set is the hour and the timezone. The scheduler's
// daily trigger owns those and is tuned in Automation settings; a second copy
// here would be a second source of truth for one setting.
//
// The email carries no attendance — the period due and a link, nothing else —
// and this screen says so, because the people being added to a mailing list
// deserve to know what will be in it.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { BellRing, Loader2, Plus, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Input } from '@/features/shared/components/ui/input';
import { Label } from '@/features/shared/components/ui/label';
import { Switch } from '@/features/shared/components/ui/switch';
import { Skeleton } from '@/features/shared/components/ui/skeleton';
import { getApiErrorMessage } from '@/lib/apiError';
import {
  getReminderSettings, updateReminderSettings,
  type ReminderRecipient, type ReminderRole, type ReminderSettings,
} from '@/services/attendanceService';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** A shape check, not a validator — the server validates the address. */
export const looksLikeEmail = (s: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());

/**
 * canEnable reports whether the reminder may be switched on.
 *
 * Refused with nobody to tell, rather than accepted and silently doing nothing
 * every week: the sweep would skip a tenant with no recipients and no screen
 * would ever say why.
 */
export const canEnable = (recipients: ReminderRecipient[]): boolean => recipients.length > 0;

/**
 * describeThreshold puts the freshness rule in words, because "1" on its own
 * does not tell anybody what will happen.
 *
 * One day is the decision taken — any gap at all. "Current" means attendance
 * covers up to yesterday; today is not over, so a morning pull cannot contain
 * it.
 */
export const describeThreshold = (days: number, weekday: number): string => {
  const day = DAYS[weekday] ?? 'Friday';
  if (days <= 1) {
    return `On ${day}, if attendance is not up to yesterday.`;
  }
  return `On ${day}, if attendance is more than ${days} days behind.`;
};

export const UploadReminderCard: React.FC<{ canWrite: boolean }> = ({ canWrite }) => {
  const { t } = useTranslation('attendance');
  const [s, setS] = useState<ReminderSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState('');
  const [draftRole, setDraftRole] = useState<ReminderRole>('to');

  useEffect(() => {
    getReminderSettings()
      .then(setS)
      .catch(() => setS(null))
      .finally(() => setLoading(false));
  }, []);

  const save = async (patch: Partial<ReminderSettings>) => {
    if (!s) return;
    setSaving(true);
    try {
      setS(await updateReminderSettings(patch));
    } catch (e) {
      // The server refuses by name — "add at least one recipient before
      // switching the reminder on" — and that sentence is better than ours.
      toast.error(getApiErrorMessage(e, t('reminder_save_failed', 'Could not save the reminder settings.')));
    } finally {
      setSaving(false);
    }
  };

  const addRecipient = () => {
    if (!s || !looksLikeEmail(draft)) return;
    const next = [...s.recipients, { email: draft.trim(), role: draftRole }];
    setDraft('');
    void save({ recipients: next });
  };

  const removeRecipient = (email: string) => {
    if (!s) return;
    const next = s.recipients.filter((r) => r.email !== email);
    // Removing the last recipient would leave the reminder on with nobody to
    // tell, which the server refuses — so turn it off in the same breath
    // rather than showing a refusal somebody did not ask for.
    void save(next.length === 0 && s.enabled ? { recipients: next, enabled: false } : { recipients: next });
  };

  if (loading) return <Skeleton className="h-64 w-full rounded-xl" />;
  if (!s) {
    return (
      <Card><CardContent className="py-6 text-sm text-muted-foreground">
        {t('reminder_unavailable', 'The upload reminder settings are unavailable.')}
      </CardContent></Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <BellRing className="h-4 w-4 text-primary-500" aria-hidden />
          {t('reminder_title', 'Upload reminder')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-sm text-muted-foreground">
          {t('reminder_explainer',
            'A nudge to whoever pulls the attendance file, when attendance is not current. '
            + 'The email contains no attendance — just how far behind it is and a link.')}
        </p>

        <div className="flex items-center justify-between gap-4">
          <div className="space-y-0.5">
            <Label htmlFor="reminder-enabled">{t('reminder_enabled', 'Send the reminder')}</Label>
            <p className="text-xs text-muted-foreground">
              {describeThreshold(s.stale_after_days, s.nudge_weekday)}
            </p>
          </div>
          <Switch
            id="reminder-enabled" checked={s.enabled}
            disabled={!canWrite || saving || (!s.enabled && !canEnable(s.recipients))}
            onCheckedChange={(v) => void save({ enabled: v })}
          />
        </div>
        {!canEnable(s.recipients) && !s.enabled && (
          <p className="text-xs text-amber-700 dark:text-amber-300">
            {t('reminder_needs_recipient', 'Add somebody to tell before switching it on.')}
          </p>
        )}

        <div className="space-y-2">
          <Label>{t('reminder_recipients', 'Who to tell')}</Label>
          {s.recipients.length === 0 && (
            <p className="text-xs text-muted-foreground">
              {t('reminder_no_recipients', 'Nobody yet.')}
            </p>
          )}
          <ul className="space-y-1.5">
            {s.recipients.map((r) => (
              <li key={r.email} className="flex items-center gap-2 text-sm">
                <span className="text-foreground">{r.email}</span>
                <span className={`rounded px-1.5 py-0.5 text-xs ${r.role === 'cc'
                  ? 'bg-muted text-muted-foreground' : 'bg-primary-500/10 text-primary-600 dark:text-primary-400'}`}>
                  {r.role === 'cc'
                    ? t('reminder_role_cc', 'copied')
                    : t('reminder_role_to', 'asked to act')}
                </span>
                {canWrite && (
                  <button type="button" onClick={() => removeRecipient(r.email)} disabled={saving}
                    className="ml-auto text-muted-foreground hover:text-danger"
                    aria-label={t('reminder_remove', 'Remove {{email}}', { email: r.email })}>
                    <X className="h-4 w-4" />
                  </button>
                )}
              </li>
            ))}
          </ul>
          {canWrite && (
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <Input
                className="max-w-xs" type="email" value={draft} placeholder="hr@company.co.tz"
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addRecipient(); } }}
                aria-label={t('reminder_add_email', 'Email address')}
              />
              <select
                className="rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground"
                value={draftRole} onChange={(e) => setDraftRole(e.target.value as ReminderRole)}
                aria-label={t('reminder_add_role', 'Role')}
              >
                <option value="to">{t('reminder_role_to', 'asked to act')}</option>
                <option value="cc">{t('reminder_role_cc', 'copied')}</option>
              </select>
              <Button size="sm" variant="outline" onClick={addRecipient}
                disabled={saving || !looksLikeEmail(draft)}>
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
                {t('reminder_add', 'Add')}
              </Button>
            </div>
          )}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="reminder-day">{t('reminder_day', 'Which day')}</Label>
            <select
              id="reminder-day" value={s.nudge_weekday} disabled={!canWrite || saving}
              className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground"
              onChange={(e) => void save({ nudge_weekday: Number(e.target.value) })}
            >
              {DAYS.map((d, i) => <option key={d} value={i}>{t(`day_${i}`, d)}</option>)}
            </select>
            <p className="text-xs text-muted-foreground">
              {t('reminder_day_hint',
                'The day you normally pull the file. A month that has ended is asked for on any day — '
                + 'a new month\'s export never contains the previous one.')}
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="reminder-stale">{t('reminder_stale', 'Days behind before it fires')}</Label>
            <Input
              id="reminder-stale" type="number" min={1} max={60} value={s.stale_after_days}
              disabled={!canWrite || saving}
              onChange={(e) => {
                const n = Number(e.target.value);
                if (Number.isFinite(n) && n >= 1 && n <= 60) void save({ stale_after_days: n });
              }}
            />
            <p className="text-xs text-muted-foreground">
              {t('reminder_stale_hint', '1 means any gap at all.')}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default UploadReminderCard;
